import { Editor } from '../core/Editor';

export class Exporter {
  /**
   * Generates a composite canvas of all visible layers
   */
  public static getCompositeCanvas(
    editor: Editor,
    targetWidth?: number,
    targetHeight?: number,
    preserveTransparency: boolean = true,
    bgColor: string = '#ffffff'
  ): HTMLCanvasElement {
    const w = targetWidth || editor.width;
    const h = targetHeight || editor.height;

    const outCanvas = document.createElement('canvas');
    outCanvas.width = w;
    outCanvas.height = h;
    const ctx = outCanvas.getContext('2d');
    if (!ctx) return outCanvas;

    // Fill background if not preserving transparency or if format doesn't support alpha
    if (!preserveTransparency) {
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, w, h);
    }

    const scaleX = w / editor.width;
    const scaleY = h / editor.height;

    ctx.save();
    ctx.scale(scaleX, scaleY);

    // Draw all visible layers from bottom to top
    editor.layers.forEach(layer => {
      if (!layer.visible) return;
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      ctx.globalCompositeOperation = layer.blendMode;
      ctx.drawImage(layer.canvas, layer.x, layer.y);
      ctx.restore();
    });

    ctx.restore();
    return outCanvas;
  }

  /**
   * Export to standard formats
   */
  public static async exportImage(
    editor: Editor,
    format: 'png' | 'jpeg' | 'webp' | 'avif' | 'svg' | 'ico' | 'bmp',
    filename: string = 'image',
    quality: number = 0.92,
    targetWidth?: number,
    targetHeight?: number,
    preserveTransparency: boolean = true
  ): Promise<void> {
    const isLossless = format === 'png' || format === 'bmp' || format === 'ico';
    const allowAlpha = format === 'png' || format === 'webp' || format === 'avif' || format === 'svg' || format === 'ico';
    const shouldKeepAlpha = allowAlpha && preserveTransparency;

    const canvas = this.getCompositeCanvas(
      editor,
      targetWidth,
      targetHeight,
      shouldKeepAlpha,
      '#ffffff'
    );

    if (format === 'svg') {
      const dataUri = canvas.toDataURL('image/png');
      const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}" viewBox="0 0 ${canvas.width} ${canvas.height}">
  <image href="${dataUri}" width="${canvas.width}" height="${canvas.height}"/>
</svg>`;
      const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
      this.downloadBlob(blob, `${filename}.svg`);
      return;
    }

    if (format === 'ico') {
      // Create a 32x32 or 48x48 icon
      const icoCanvas = document.createElement('canvas');
      icoCanvas.width = 32;
      icoCanvas.height = 32;
      const iCtx = icoCanvas.getContext('2d');
      if (iCtx) {
        iCtx.drawImage(canvas, 0, 0, 32, 32);
      }
      icoCanvas.toBlob(blob => {
        if (blob) this.downloadBlob(blob, `${filename}.ico`);
      }, 'image/x-icon');
      return;
    }

    const mime = `image/${format}`;
    canvas.toBlob(
      blob => {
        if (blob) {
          this.downloadBlob(blob, `${filename}.${format === 'jpeg' ? 'jpg' : format}`);
        }
      },
      mime,
      quality
    );
  }

  /**
   * Save Project as JSON (all layers, positions, opacities, content)
   */
  public static exportProject(editor: Editor, filename: string = 'project'): void {
    const project = {
      version: '1.0',
      name: filename,
      width: editor.width,
      height: editor.height,
      timestamp: Date.now(),
      layers: editor.layers.map(l => ({
        id: l.id,
        name: l.name,
        type: l.type,
        visible: l.visible,
        locked: l.locked,
        opacity: l.opacity,
        blendMode: l.blendMode,
        x: l.x,
        y: l.y,
        width: l.width,
        height: l.height,
        dataUri: l.canvas.toDataURL(),
        textData: l.textData
      }))
    };

    const json = JSON.stringify(project, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    this.downloadBlob(blob, `${filename}.photodrb`);
  }

  /**
   * Browser file download helper
   */
  public static downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  }
}
